import { type DefaultRootState } from 'react-redux';
import {
  createSelector,
  createSlice,
  isAnyOf,
  type PayloadAction,
} from '@reduxjs/toolkit';
import {
  createApi,
  fetchBaseQuery,
  type BaseQueryApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {
  hasUsableAuthenticityToken,
  isUnauthorizedError,
  selectHeldAuthToken,
  type SessionProbe,
} from '@bbkAdminRedux/rtkq/auth-status';
import {
  SignInType,
  type GetSignInUrlArgs,
  type SessionMetadata,
  type SessionMetadataResponse,
  type SignInArgs,
  type SignInTokens,
} from '@bbkAdminRedux/rtkq/auth-types';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { clearSessionMetadata } from '@bbkAdminRedux/rtkq/clear-session-metadata';
import {
  preauthCompanySlice,
  type BbkClientConfig,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import { BrightbackAuth } from '@bbkAdminUtils/brightback-auth';
import { hardRedirect } from '@bbkAdminUtils/browser-navigation';
import { getNowInSeconds } from '@bbkAdminUtils/utils';

// Re-export for backward compatibility
export {
  type GetSignInUrlArgs,
  type SignInArgs,
  type SignInTokens,
  SignInType,
} from '@bbkAdminRedux/rtkq/auth-types';

export const UNSUPPORTED_AUTH_SESSION_ERROR =
  'Stored auth session type is unsupported';

export const CONFIG_NOT_LOADED_ERROR = 'Config is not loaded';

export const NOT_SIGNED_IN_ERROR = 'User is not signed in';

const SESSION_REFRESH_URL = '/api/v1/auth/session/refresh';
const SESSION_UPGRADE_URL = '/api/v1/auth/session/upgrade';
const SESSION_LOGOUT_URL = '/api/v1/auth/session/logout';
const AUTHENTICITY_TOKEN_HEADER = 'BBK-Authenticity-Token';

const getClientConfig = (api: BaseQueryApi): BbkClientConfig => {
  const state = api.getState() as DefaultRootState;
  const { data: config } =
    preauthCompanySlice.endpoints.getClientConfig.select()(state);

  if (!config) {
    throw new Error(CONFIG_NOT_LOADED_ERROR);
  }

  return config;
};

const createAuth = (api: BaseQueryApi): BrightbackAuth =>
  new BrightbackAuth(getClientConfig(api));

/**
 * Resolves the auth flow for a backend session `type` tag. Only `dev` and
 * `cbidp` (Chargebee IDP) remain supported. Legacy Cognito-backed sessions
 * (`google` and username sessions, which were persisted as `null`) resolve to
 * `undefined` so callers treat them as stale and force a fresh sign-in.
 */
export const resolveSigninTypeFromTokenType = (
  tokenType: 'cbidp' | 'dev' | 'google' | null | undefined
): SignInType | undefined => {
  switch (tokenType) {
    case 'dev':
      return SignInType.DEV;
    case 'cbidp':
      return SignInType.CHARGEBEE;
    default:
      return undefined;
  }
};

/** Map the backend session response (`{ type, exp }`) to client metadata. */
const toSessionMetadata = (
  response: SessionMetadataResponse
): SessionMetadata => ({
  type: response.type === 'dev' ? SignInType.DEV : SignInType.CHARGEBEE,
  exp: response.exp,
});

/**
 * Attach the CSRF header to the backend-owned session mutations
 * (`upgrade`/`refresh`/`logout`). The token comes from the in-memory holder
 * captured during sign-in, falling back to the `/api/current_user` probe. Read
 * defensively so isolated tests without the current-user slice do not throw.
 */
const attachAuthenticityToken = (
  headers: Headers,
  state: DefaultRootState
): Headers => {
  const heldToken = selectHeldAuthToken(state);
  if (heldToken) {
    headers.set(AUTHENTICITY_TOKEN_HEADER, heldToken);
    return headers;
  }
  try {
    const { data: currentUser } =
      bbkApiSlice.endpoints.getCurrentUser.select()(state);
    if (hasUsableAuthenticityToken(currentUser)) {
      headers.set(AUTHENTICITY_TOKEN_HEADER, currentUser.authenticity_token);
    }
  } catch {
    // current-user slice not registered in this store (e.g. focused tests).
  }
  return headers;
};

const hasAuthenticityToken = (api: BaseQueryApi): boolean => {
  const state = api.getState() as DefaultRootState;
  if (selectHeldAuthToken(state)) {
    return true;
  }
  try {
    const { data: currentUser } =
      bbkApiSlice.endpoints.getCurrentUser.select()(state);
    return hasUsableAuthenticityToken(currentUser);
  } catch {
    // current-user slice not registered in this store (e.g. focused tests).
    return false;
  }
};

/**
 * Makes sure a CSRF token is available before a session request goes out, and
 * reports what the probe learned about the session on the way.
 *
 * Sign-out is reachable from pages that render outside the authenticated shell
 * — `/logout` (linked from the company selector) mounts a bare panel and never
 * queries `/api/current_user` — so `attachAuthenticityToken` would find nothing
 * to send and the CSRF-protected endpoint would reject the request with a 403,
 * stranding the user in a signed-in state. Fetching the current user here
 * populates the token for the request that follows. No-op when a token is
 * already held.
 *
 * Never returns `pending`: this awaits the probe.
 */
const probeSession = async (api: BaseQueryApi): Promise<SessionProbe> => {
  if (hasAuthenticityToken(api)) {
    return 'present';
  }
  try {
    await api
      .dispatch(bbkApiSlice.endpoints.getCurrentUser.initiate())
      .unwrap();
  } catch (error) {
    // A 401 is the server's authoritative "no session". Anything else — 5xx,
    // offline, a timeout — means we could not find out, and must not be read as
    // an answer.
    return isUnauthorizedError(error) ? 'absent' : 'unknown';
  }
  return hasAuthenticityToken(api) ? 'present' : 'absent';
};

export const brightbackAuthSlice = createApi({
  reducerPath: 'brightbackAuth-rktq',
  baseQuery: fetchBaseQuery({
    prepareHeaders: (headers, api) =>
      attachAuthenticityToken(headers, api.getState() as DefaultRootState),
  }),
  tagTypes: [],
  endpoints: (build) => ({
    openSignIn: build.mutation<void, GetSignInUrlArgs>({
      queryFn: async (args, api) => {
        try {
          const auth = createAuth(api);
          const url = await auth.getSignInUrl(args);
          // FIXME: side effect
          window.location.assign(url);
          // next line doesn't do anything, because window.location.assign redirects to different url
          // but to comply with rtkq contract we need to return it
          return { data: undefined };
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          };
        }
      },
    }),

    signIn: build.mutation<SignInTokens, SignInArgs>({
      queryFn: async (args, api) => {
        try {
          const auth = createAuth(api);
          const metadata = await auth.signIn(args);
          return { data: metadata };
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          };
        }
      },
    }),

    /**
     * Idempotent bootstrap: read the existing valid session cookie and have the
     * backend rewrite it with HttpOnly attributes, returning fresh metadata.
     * Called once after `/api/current_user` confirms an authenticated session.
     */
    upgradeSession: build.mutation<SessionMetadata, void>({
      query: () => ({ url: SESSION_UPGRADE_URL, method: 'POST' }),
      transformResponse: toSessionMetadata,
    }),

    /**
     * Backend-managed refresh: sends no credential in the body. The backend
     * reads the session cookie, calls CB IDP introspection, and rotates the
     * cookie, returning updated metadata. A 401 is terminal; 429/5xx are
     * transient and surface as errors carrying the HTTP status.
     */
    doRefreshTokens: build.mutation<SessionMetadata, void>({
      query: () => ({ url: SESSION_REFRESH_URL, method: 'POST' }),
      transformResponse: toSessionMetadata,
    }),

    /**
     * Ends the session and navigates to `redirect`. The backend names a
     * destination of its own only when the sign-out detours through Chargebee
     * IDP, whose logout page it alone can address; where the app itself lands is
     * this caller's decision.
     *
     * `scope` controls how far the sign-out reaches. The default tears down the
     * upstream Chargebee IDP session as well, which is what the nav "Sign out"
     * means. `'local'` expires only our own session cookie — the trial-signup
     * flow needs a clean slate without signing the user out of every other
     * Chargebee product open in the browser.
     */
    signOut: build.mutation<void, { redirect: string; scope?: 'local' }>({
      queryFn: async ({ redirect, scope }, api, _extra, baseQuery) => {
        const probe = await probeSession(api);
        const result = await baseQuery({
          url: SESSION_LOGOUT_URL,
          method: 'POST',
          body: scope ? { redirect, scope } : { redirect },
        });
        if (result.error) {
          // Finish locally only on positive evidence that there is no session
          // left to end: the probe was authoritatively unauthenticated (so the
          // CSRF-protected logout was always going to 403), or the server
          // rejected the credential itself. Either way there is nothing to
          // revoke, and stranding the user on the spinner helps no one.
          //
          // A probe that could not reach the server is not evidence, which is
          // why this checks `absent` rather than "no token". On a cold
          // `/logout`, a 5xx on `/api/current_user` leaves us with no CSRF token
          // and the expected 403 from logout — a pair that looks exactly like an
          // expired session while the HttpOnly cookie is very much alive.
          //
          // Anything else — 5xx, a dropped connection — is a real failure. The
          // cookie-clearing response never arrived, so the session is still
          // live; reporting success would sign the user out in appearance only
          // and the next probe would silently restore them.
          if (probe === 'absent' || isUnauthorizedError(result.error)) {
            clearSessionMetadata(api.dispatch);
            hardRedirect(redirect);
            return { data: undefined };
          }
          return { error: result.error };
        }
        // `matchFulfilled` clears the metadata too, but only once this queryFn
        // returns — by then the navigation below has been issued and the write
        // can no longer reach storage.
        clearSessionMetadata(api.dispatch);
        const { url } = (result.data ?? {}) as { url?: string };
        hardRedirect(url ?? redirect);
        return { data: undefined };
      },
    }),
  }),
});

type AuthState = {
  auth?: SessionMetadata;
};

export const authPersistedSlice = createSlice({
  name: 'authPersisted-rtk',
  initialState: {} as AuthState,
  reducers: {
    saveTokens: (
      draftState,
      { payload: metadata }: PayloadAction<SignInTokens>
    ) => {
      // Only non-secret metadata is persisted; the JWT never leaves the backend
      // HttpOnly cookie.
      draftState.auth = { type: metadata.type, exp: metadata.exp };
    },
    clearTokens: (draftState) => {
      draftState.auth = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        brightbackAuthSlice.endpoints.signIn.matchFulfilled,
        brightbackAuthSlice.endpoints.upgradeSession.matchFulfilled,
        brightbackAuthSlice.endpoints.doRefreshTokens.matchFulfilled
      ),
      authPersistedSlice.caseReducers.saveTokens
    );
    builder.addMatcher(
      brightbackAuthSlice.endpoints.signOut.matchFulfilled,
      authPersistedSlice.caseReducers.clearTokens
    );
  },
});

export const selectAuth = (state: DefaultRootState) =>
  state[authPersistedSlice.name].auth;

export const selectSignInType = createSelector(
  selectAuth,
  (subState) => subState?.type
);

export const selectAuthExpiresIn = createSelector(selectAuth, (subState) => {
  const exp = subState?.exp;
  let expiresIn = 0;
  if (exp) {
    expiresIn = exp - getNowInSeconds();
  }
  return expiresIn > 5 ? expiresIn - 5 : 0;
});
